import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { IAdminUser } from '@apis/adminUsers';
import type { IAdminProfession } from '@apis/adminProfessions';
import { Pill } from '@components/ui/Pill/Pill';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { professionDisplayText } from '@utils/professionDisplay';
import { MailIcon, PencilIcon, SendIcon, TrashIcon } from '@/utils/icons';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import table from '../../adminTable.module.css';
import { MfaCell, StatusCell } from './MemberFactsCells';
import styles from './UsersTable.module.css';

const COLUMNS = [
  { key: 'fullName', labelKey: 'adminUsers.col.fullName', centered: false },
  { key: 'email', labelKey: 'adminUsers.col.email', centered: false },
  { key: 'phone', labelKey: 'adminUsers.col.phone', centered: false },
  { key: 'permission', labelKey: 'adminUsers.col.permission', centered: true },
  { key: 'signInMethod', labelKey: 'adminUsers.col.signInMethod', centered: true },
  { key: 'status', labelKey: 'adminUsers.col.status', centered: true },
  { key: 'mfa', labelKey: 'adminUsers.col.mfa', centered: true },
  { key: 'actions', labelKey: 'adminUsers.col.actions', centered: true },
] as const;

// Upstream role constants → i18n label keys. Unknown roles fall back to "—"
// rather than leaking a raw constant into the UI.
const ROLE_LABEL_KEYS: Record<string, string> = {
  TRUSSTOR_ADMIN: 'adminUsers.permission.systemAdmin',
  PROJECT_ADMIN: 'adminUsers.permission.projectAdmin',
  MANAGER: 'adminUsers.permission.manager',
  EXECUTIVE: 'adminUsers.permission.executive',
  VIEWER: 'adminUsers.permission.viewer',
};

/** Subtext under a viewer's role: the single permitted profession, or a count. */
function permittedProfessionsSubtext(
  t: TFunction,
  user: IAdminUser,
  professions: IAdminProfession[],
): string {
  const ids = user.permittedProfessionsIds;
  if (!ids || ids.length === 0) return '';
  if (ids.length > 1) return t('adminUsers.professionsCount', { count: ids.length });
  const profession = professions.find((p) => p._id === ids[0]);
  return profession ? professionDisplayText(t, profession) : '';
}

/** A member who has not signed in yet — the only ones an invitation can be resent to. */
function isPendingInvitation(user: IAdminUser): boolean {
  return user.status === 'invited' || user.status === 'invitationExpired';
}

interface IRowActionProps {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}

/** One icon button in the row's actions cell; its label is both tooltip and accessible name. */
function RowAction(props: IRowActionProps) {
  return (
    <Tooltip content={props.label}>
      <button
        type="button"
        className={styles.actionBtn}
        aria-label={props.label}
        disabled={props.disabled}
        onClick={props.onClick}
      >
        {props.children}
      </button>
    </Tooltip>
  );
}

interface IUsersTableProps {
  users: IAdminUser[];
  professions: IAdminProfession[];
  currentUserId: string | null;
  emptyMessage: string;
  onEdit: (user: IAdminUser) => void;
  onDelete: (user: IAdminUser) => void;
  onResetPassword: (user: IAdminUser) => void;
  onResendInvitation: (user: IAdminUser) => void;
}

export function UsersTable({
  users,
  professions,
  currentUserId,
  emptyMessage,
  onEdit,
  onDelete,
  onResetPassword,
  onResendInvitation,
}: IUsersTableProps) {
  const { t } = useTranslation();

  return (
    <ScrollArea orientation="both" className={table.tableWrap}>
      <table className={table.table}>
        <thead>
          <tr className={table.headerRow}>
            {COLUMNS.map((col) => (
              <th key={col.key} className={col.centered ? table.thCenter : table.th}>
                {t(col.labelKey)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {users.length === 0 ? (
            <tr>
              <td className={table.empty} colSpan={COLUMNS.length}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            users.map((user) => {
              const roleLabelKey = user.role ? ROLE_LABEL_KEYS[user.role] : undefined;
              const roleSubtext = permittedProfessionsSubtext(t, user, professions);
              // An admin cannot remove themselves — it would lock them out of the site.
              const isSelf = user.id === currentUserId;
              const deleteLabel = t(isSelf ? 'adminUsers.actions.deleteSelf' : 'adminUsers.actions.delete');
              // Without an address on record there is nowhere to mail a reset or an invitation.
              const noEmailLabel = user.email ? null : t('adminUsers.actions.resetPasswordNoEmail');
              // A company-account member has no password: nothing to reset, so nothing offered.
              const isSso = user.signInMethod === 'sso';
              return (
              <tr key={user.id} className={table.row}>
                <td className={table.td}>
                  <span className={styles.userCell}>
                    <span>{user.name ?? user.username}</span>
                    {isSelf && (
                      <span className={table.muted}>({t('adminUsers.self')})</span>
                    )}
                  </span>
                </td>
                <td className={user.email ? table.td : `${table.td} ${table.muted}`}>
                  {user.email ?? '—'}
                </td>
                <td className={user.phone ? table.td : `${table.td} ${table.muted}`}>
                  {user.phone ?? '—'}
                </td>
                <td className={roleLabelKey ? table.tdCenter : `${table.tdCenter} ${table.muted}`}>
                  {roleLabelKey ? (
                    <span className={styles.stackCell}>
                      <span>{t(roleLabelKey)}</span>
                      {roleSubtext && <span className={styles.subtext}>{roleSubtext}</span>}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className={isSso ? table.tdCenter : `${table.tdCenter} ${table.muted}`}>
                  {isSso ? (
                    <Pill className={styles.ssoPill}>{t('adminUsers.signInMethod.sso')}</Pill>
                  ) : (
                    t('adminUsers.signInMethod.password')
                  )}
                </td>
                <StatusCell user={user} />
                <MfaCell user={user} />
                <td className={table.tdCenter}>
                  <span className={styles.actionsCell}>
                    {isPendingInvitation(user) && (
                      <RowAction
                        label={noEmailLabel ?? t('adminUsers.actions.resendInvitation')}
                        disabled={!user.email}
                        onClick={() => onResendInvitation(user)}
                      >
                        <SendIcon size={15} />
                      </RowAction>
                    )}
                    {isSso ? null : (
                      <RowAction
                        label={noEmailLabel ?? t('adminUsers.actions.resetPassword')}
                        disabled={!user.email}
                        onClick={() => onResetPassword(user)}
                      >
                        <MailIcon size={15} />
                      </RowAction>
                    )}
                    <RowAction label={t('adminUsers.actions.edit')} onClick={() => onEdit(user)}>
                      <PencilIcon size={15} />
                    </RowAction>
                    <RowAction label={deleteLabel} disabled={isSelf} onClick={() => onDelete(user)}>
                      <TrashIcon size={15} />
                    </RowAction>
                  </span>
                </td>
              </tr>
              );
            })
          )}
        </tbody>
      </table>
    </ScrollArea>
  );
}
